/* eslint-disable no-underscore-dangle, react/prop-types */
import React, { useCallback, useContext, useMemo, useState } from "react";
import { CSSTransitionGroup } from "react-transition-group";

import PropTypes from "prop-types";
import PageTitle from "src/Layout/AppMain/PageTitle";
import DataTableControlled from "src/Components/DataTableControlled";
import { getListingData } from "src/Components/DataTableControlled/utils";
import {
  AsyncAutocompleteSelectFilter,
  DateFilter,
  IntIdFilter,
} from "src/Components/DataTable/filters";
import {
  getDateCell,
  mapValueFromOptions,
} from "src/Components/DataTable/commonCells";
import {
  EMPLOYEE_MANAGEMENT_SERVICE,
  NOTIFICATION_SERVICE,
  NOTIFICATION_V2_SERVICE,
  restApiRequest,
} from "src/utils/Api";
import {
  godPermission,
  notificationAppMessagePermissionDelete,
  notificationAppMessagePermissionWrite,
} from "src/utils/RoleBasedSecurity/permissions";
import ActionColumn from "src/Components/DataTable/actionColumn";
import {
  getReceiverScope,
  RECEIVER_SCOPE_ALL,
  RECEIVER_SCOPE_COMPANY,
  RECEIVER_SCOPE_EMPLOYEE,
  RECEIVER_SCOPE_EMPLOYEE_GROUP,
  RECEIVER_SCOPE_ORGANIZATION_UNIT,
} from "src/Pages/Notification/AppMessages/Edit/edit";
import __ from "src/utils/Translations";
import DefaultTooltip from "src/Components/Tooltips/DefaultTooltip";
import useOperators from "src/utils/hooks/operator/useOperators";
import useCompanies from "src/utils/hooks/company/useCompanies";
import useAhrs from "src/utils/hooks/company/useAhrs";
import useEmployees from "src/utils/hooks/company/useEmployees";
import useEmployeeGroups from "src/utils/hooks/company/useEmployeeGroups";
import useOrganizationUnits from "src/utils/hooks/company/useOrganizationUnits";
import { dynamicNotification } from "src/utils/Notifications";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";
import RbsContext from "src/utils/RoleBasedSecurity/RbsContext";
import getUserConfirmationPopup from "src/utils/system/DOM/getUserConfirmationPopup";

const MAX_ITEMS_PER_REQUEST = 100;
const RECEIVER_FILTER_ID = "receiver";
const STATUS_ACTIVE = "active";
const STATUS_CANCELLED = "cancelled";

const getStatusFromDeletedAt = (deletedAt) =>
  deletedAt ? STATUS_CANCELLED : STATUS_ACTIVE;

const mapEmployeeToOption = ({ id, firstName, lastName, username }) => ({
  value: id,
  label: `${firstName} ${lastName}${username ? ` (${username})` : ""}`,
});

const getReceiverEmployeesOptionsFetchMethod =
  (currValue, additionalFilterParams) =>
  async (inputValue = "", page = 1) => {
    const itemsPerPage = 100;
    const params = {
      page,
      itemsPerPage,
      ...additionalFilterParams,
    };

    if (inputValue) {
      params.or_searchByName = [inputValue];
    } else if (currValue && !Array.isArray(currValue)) {
      params.id = currValue;
    } else if (Array.isArray(currValue) && currValue.length) {
      params.id = currValue;
    }

    const fetchByName = restApiRequest(
      EMPLOYEE_MANAGEMENT_SERVICE,
      "/employees",
      "GET",
      { params },
      []
    )
      .then((result) => result.map(mapEmployeeToOption))
      .catch((e) => {
        newRelicErrorReport(
          e,
          "Pages/Notification/AppMessages/List/list.js - 82"
        );
        return [];
      });

    const promises = [fetchByName];

    if (inputValue) {
      const fetchByUsername = restApiRequest(
        EMPLOYEE_MANAGEMENT_SERVICE,
        "/employees",
        "GET",
        {
          params: {
            username: inputValue,
            page,
            itemsPerPage,
            ...additionalFilterParams,
          },
        },
        []
      )
        .then((result) => result.map(mapEmployeeToOption))
        .catch((e) => {
          newRelicErrorReport(
            e,
            "Pages/Notification/AppMessages/List/list.js - 108"
          );
          return [];
        });
      promises.push(fetchByUsername);
    }

    const responses = await Promise.all(promises);
    const result = responses.flat();

    return result.filter(
      (value, index, self) =>
        self.findIndex((el) => el.value === value.value) === index
    );
  };

const normalizeMessageIds = (response) => {
  if (!Array.isArray(response)) {
    return [];
  }

  return response?.map((item) => item?.id);
};

export default function AppMessagesList({
  company,
  companyScope,
  getUrlToForm,
  breadcrumbs,
}) {
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [createdByIds, setCreatedByIds] = useState([]);
  const [companyIds, setCompanyIds] = useState([]);
  const [employeeIds, setEmployeeIds] = useState([]);
  const [employeeGroupIds, setEmployeeGroupIds] = useState([]);
  const [organizationUnitIds, setOrganizationUnitIds] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const rbsContext = useContext(RbsContext);
  const isAhr = rbsContext.userInfo.isAhr();
  const canDeleteAppMessage = useMemo(() => {
    const permissions = rbsContext.userInfo.getPermissions();
    return (
      permissions.includes(notificationAppMessagePermissionDelete) ||
      permissions.includes(godPermission)
    );
  }, [rbsContext.userInfo]);

  const ReceiverFilter = useMemo(
    () =>
      AsyncAutocompleteSelectFilter(
        (filterValue) =>
          getReceiverEmployeesOptionsFetchMethod(
            filterValue,
            companyScope && company ? { companyId: company } : undefined
          ),
        true,
        100
      ),
    [companyScope, company]
  );

  const fetchData = useCallback(
    async (filters, page, pageSize, sort) => {
      const receiverFilter = filters.find(
        ({ id }) => id === RECEIVER_FILTER_ID
      );
      const requestFilters = filters.filter(
        ({ id }) => id !== RECEIVER_FILTER_ID
      );

      if (receiverFilter?.value) {
        try {
          const messageIdsResponse = await restApiRequest(
            NOTIFICATION_V2_SERVICE,
            `/messages/get-by-employee-id-for-message-ids/${receiverFilter.value}`,
            "GET",
            {},
            []
          );
          const messageIds = normalizeMessageIds(messageIdsResponse);

          if (!messageIds.length) {
            setData([]);
            setCount(0);
            setCompanyIds([]);
            setEmployeeGroupIds([]);
            setOrganizationUnitIds([]);
            setEmployeeIds([]);
            setCreatedByIds([]);
            return;
          }

          requestFilters.push({ id: "id", value: messageIds });
        } catch (e) {
          newRelicErrorReport(
            e,
            "Pages/Notification/AppMessages/List/list.js - 115"
          );
          dynamicNotification(
            e.message || __("Nie udało się pobrać wiadomości dla odbiorcy"),
            "error"
          );
          setData([]);
          setCount(0);
          return;
        }
      }

      if (companyScope) {
        requestFilters.push({ id: "companyId", value: company });
      }
      const { data: newData, count: newCount } = await getListingData(
        NOTIFICATION_SERVICE,
        "/app-messages/groupByMultiselect",
        requestFilters,
        page,
        pageSize,
        sort || defaultSorted,
        {
          dateFilters: ["visibleFrom", "createdAt"],
          params: { withDeleted: true },
        },
        mockData
      );
      setData(
        newData.map((el) => ({
          ...el,
          status: getStatusFromDeletedAt(el.deletedAt),
          receiverScope: getReceiverScope(el),
          receiver: el.multiselectId
            ? el.employeesId
            : el.employeeId ||
              el.employeeGroupId ||
              el.organizationUnitId ||
              el.companyId,
        }))
      );
      setCount(newCount);
      const companies = [];
      const employeeGroups = [];
      const organizationUnits = [];
      let employees = [];
      const authors = [];
      newData.forEach(
        ({
          companyId,
          employeeId,
          employeesId,
          employeeGroupId,
          organizationUnitId,
          createdBy,
        }) => {
          if (companyId) {
            companies.push(companyId);
          }
          if (employeeId) {
            if (employeesId?.length) {
              employees = [...employees, ...employeesId];
            } else {
              employees.push(employeeId);
            }
          }
          if (employeeGroupId) {
            employeeGroups.push(employeeGroupId);
          }
          if (organizationUnitId) {
            organizationUnits.push(organizationUnitId);
          }
          if (createdBy) {
            authors.push(createdBy);
          }
        }
      );

      setCompanyIds(companies);
      setEmployeeGroupIds(employeeGroups);
      setOrganizationUnitIds(organizationUnits);
      setEmployeeIds([...new Set(employees)]); // remove duplicates
      setCreatedByIds([...new Set(authors)]);
    },
    [companyScope, company]
  );

  const operators = useOperators(
    true,
    "id",
    createdByIds,
    false,
    !createdByIds.length
  );
  const ahrs = useAhrs(true, "id", createdByIds, false, !createdByIds.length);
  const companies = useCompanies({
    asOption: true,
    filterField: "id",
    filterValue: companyIds,
    noFetching: !companyIds.length,
  });
  const employeeGroups = useEmployeeGroups(
    true,
    "id",
    employeeGroupIds,
    false,
    !employeeGroupIds.length
  );
  const organizationUnits = useOrganizationUnits(
    true,
    "id",
    organizationUnitIds,
    false,
    !organizationUnitIds.length
  );
  const employeesData = useEmployees(
    false,
    "id",
    employeeIds,
    false,
    !employeeIds.length,
    employeeIds.length < MAX_ITEMS_PER_REQUEST
      ? employeeIds.length
      : MAX_ITEMS_PER_REQUEST
  );
  const employees = useMemo(
    () => employeesData.map(mapEmployeeToOption),
    [employeesData]
  );

  const createdByOptions = [...operators, ...ahrs];
  const receiverOptions = [
    ...companies,
    ...employeeGroups,
    ...organizationUnits,
    ...employees,
  ];

  const deleteAppMessage = async (id) => {
    try {
      await restApiRequest(
        NOTIFICATION_SERVICE,
        `/app-messages/${id}`,
        "DELETE",
        { returnNull: true },
        null
      );
      dynamicNotification(__("Pomyślnie usunięto powiadomienie"));
      setReloadKey((prev) => prev + 1);
    } catch (e) {
      console.error(e);
      newRelicErrorReport(
        e,
        "Pages/Notification/AppMessages/List/list.js - 361"
      );
      dynamicNotification(
        e.message || __("Nie udało się usunąć powiadomienia"),
        "error"
      );
    }
  };

  return (
    <>
      <CSSTransitionGroup
        component="div"
        transitionName="TabsAnimation"
        transitionAppear
        transitionAppearTimeout={0}
        transitionEnter={false}
        transitionLeave={false}
      >
        <PageTitle
          heading={__("Lista wiadomości wewnątrzsystemowych")}
          breadcrumbs={breadcrumbs}
        />
        <DataTableControlled
          id="agreementsListing"
          fetchData={fetchData}
          data={data}
          count={count}
          reloadDependencies={[reloadKey]}
          filterable
          buttons={[
            {
              id: "addAppMessage",
              color: "primary",
              href: getUrlToForm(-1),
              text: `+ ${__("Dodaj wiadomość")}`,
              permission: notificationAppMessagePermissionWrite,
            },
          ]}
          columns={[
            {
              Header: "ID",
              accessor: "id",
              filterable: false,
              Filter: IntIdFilter,
            },
            {
              Header: "Zakres",
              accessor: "receiverScope",
              filterable: false,
              sortable: false,
              Cell: mapValueFromOptions(receiverScopeOptions, "receiverScope"),
            },
            {
              Header: (
                <>
                  {__("Odbiorca")}{" "}
                  <DefaultTooltip
                    id="filter-tooltip-receiver"
                    content={__(
                      "Aby filtrować, wpisz trzy pierwsze litery imienia, nazwiska lub loginu"
                    )}
                  />
                </>
              ),
              additionalHeader: true,
              accessor: "receiver",
              sortable: false,
              Filter: ReceiverFilter,
              Cell: mapValueFromOptions(receiverOptions, "receiver"),
            },
            {
              Header: (
                <>
                  {__("Zlecający")}{" "}
                  <DefaultTooltip
                    id="filter-tooltip-created-by"
                    content={
                      <>
                        {__("Aby filtrować wpisz id operatora lub ahr'a.")}
                        <br />
                        {__(
                          "Brak nazwy zlecającego może oznaczać że nie masz dostępu do danego zasobu."
                        )}
                      </>
                    }
                  />
                </>
              ),
              additionalHeader: true,
              accessor: "createdBy",
              sortable: false,
              filterable: false,
              Cell: mapValueFromOptions(createdByOptions, "createdBy"),
            },
            {
              Header: "Tytuł",
              accessor: "subject",
              sortable: false,
            },
            {
              Header: "Zawartość",
              accessor: "message",
              sortable: false,
              filterable: false,
              Cell: (cellData) => {
                let value = cellData.row._original[cellData.column.id];
                if (value) {
                  value = value.replace(/(<([^>]+)>)/gi, "");
                }
                if (value && value.length > 258) {
                  value = `${value.slice(0, 255)}...`;
                }
                return value;
              },
            },
            {
              Header: "Wyświetlaj od/do",
              accessor: "visibleFrom",
              Filter: DateFilter(true),
              Cell: getDateCell("visibleFrom", true),
              minWidth: 150,
            },
            {
              Header: "Data utworzenia",
              accessor: "createdAt",
              Filter: DateFilter(true),
              Cell: getDateCell("createdAt", true),
              minWidth: 150,
            },
            {
              Header: "Status",
              accessor: "status",
              filterable: false,
              sortable: false,
              Cell: mapValueFromOptions(statusOptions, "status"),
            },
            {
              Header: "Akcja",
              accessor: "action",
              filterable: false,
              sortable: false,
              Cell: ({ row }) => {
                const isCancelled = Boolean(row._original.deletedAt);
                return (
                  <div className="d-block w-100 text-center">
                    <ActionColumn
                      data={row._original}
                      buttons={[
                        {
                          id: "appMessageEdit",
                          className: "m-1",
                          color: "link",
                          label: isCancelled ? "Podgląd" : "Edytuj",
                          href: getUrlToForm(row._original.id),
                        },
                        {
                          id: "appMessageDelete",
                          className: "m-1",
                          color: "link",
                          label: "Usuń",
                          doNotDisplay:
                            isCancelled || isAhr || !canDeleteAppMessage,
                          onClick: () => {
                            getUserConfirmationPopup(
                              __("Czy na pewno chcesz usunąć powiadomienie?"),
                              (confirm) =>
                                confirm && deleteAppMessage(row._original.id),
                              __("Usuwanie powiadomienia")
                            );
                          },
                        },
                      ]}
                    />
                  </div>
                );
              },
            },
          ]}
        />
      </CSSTransitionGroup>
    </>
  );
}

const defaultSorted = {
  key: "visibleFrom",
  value: "desc",
};
const receiverScopeOptions = [
  { value: RECEIVER_SCOPE_ALL, label: "Wszyscy" },
  { value: RECEIVER_SCOPE_COMPANY, label: "Firma" },
  { value: RECEIVER_SCOPE_EMPLOYEE_GROUP, label: "Grupa pracownicza" },
  { value: RECEIVER_SCOPE_ORGANIZATION_UNIT, label: "Jednostka organizacyjna" },
  { value: RECEIVER_SCOPE_EMPLOYEE, label: "Wybrani pracownicy" },
];
const statusOptions = [
  { value: STATUS_ACTIVE, label: "Aktywne" },
  { value: STATUS_CANCELLED, label: "Anulowane" },
];

const mockData = [
  {
    companyId: "Firma 1",
    createdAt: "2020-12-17T10:44:00+00:00",
    createdBy: "Administrator 1",
    id: 1,
    subject: "Ważna wiadomość",
    message:
      "On the other hand, we denounce with righteous indignation and dislike men who are so...",
    visibleFrom: "2020-12-17T10:44:00+00:00",
    deletedAt: null,
  },
  {
    employeeId: "Jan Kowlaski",
    createdAt: "2020-12-17T10:44:00+00:00",
    createdBy: "Administrator 2",
    id: 2,
    subject: "Pilne powiadomienie",
    message:
      "On the other hand, we denounce with righteous indignation and dislike men who are so...",
    visibleFrom: "2020-12-17T10:44:00+00:00",
    deletedAt: "2020-12-18T10:44:00+00:00",
  },
  {
    employeeGroupId: "Nowi użytkownicy",
    createdAt: "2020-12-17T10:44:00+00:00",
    createdBy: "Administrator 2",
    id: 3,
    subject: "Witaj w serwisie!",
    message:
      "On the other hand, we denounce with righteous indignation and dislike men who are so...",
    visibleFrom: "2020-12-17T10:44:00+00:00",
    deletedAt: null,
  },
];
AppMessagesList.propTypes = {
  company: PropTypes.string,
  companyScope: PropTypes.bool,
  getUrlToForm: PropTypes.func.isRequired,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      link: PropTypes.string,
    })
  ).isRequired,
};
AppMessagesList.defaultProps = {
  company: null,
  companyScope: false,
};
